import {
  Badge,
  Button,
  cn,
  ConfirmableDeleteButton,
  Draggable,
  Icon,
  Text,
} from '@risksmart-app/atomic-ui';
import { type MouseEvent } from 'react';

import { getFieldTypeLabel } from '../constants';

export const FieldCard = ({
  name,
  type,
  required = false,
  readOnly = false,
  hidden = false,
  hasConditionalLogic = false,
  subtitle,
  onEdit,
  onDelete,
  fieldTypeOptions,
}: {
  name: string;
  type: string;
  required?: boolean;
  readOnly?: boolean;
  hidden?: boolean;
  hasConditionalLogic?: boolean;
  subtitle?: string;
  onEdit?: () => void;
  onDelete?: () => void;
  fieldTypeOptions?: { value: string; label: string }[];
}) => {
  const handleRowClick = (event: MouseEvent<HTMLDivElement>) => {
    if (!onEdit) {
      return;
    }
    // The drag handle, edit and delete buttons are real <button>s nested in the
    // row. The drag handle in particular does not stop propagation, so without
    // this guard grabbing a field to reorder it would also open the editor.
    if (event.target instanceof Element && event.target.closest('button')) {
      return;
    }
    onEdit();
  };

  return (
    <div
      className={cn(
        'flex items-center gap-3 rounded-lg border border-neutral-border bg-neutral p-3 group/field-card',
        hidden && 'opacity-50',
        // draggableItemVariants resolves the wrapper cursor to `auto` when a
        // drag handle is present, so the clickable row has to opt back in.
        onEdit && 'cursor-pointer'
      )}
      onClick={handleRowClick}
    >
      <Draggable.DragHandle className={'text-neutral-active'} />
      <div className={cn('flex flex-col gap-0.5 flex-1')}>
        <div className={cn('flex items-center gap-2')}>
          <Text
            preset={'body-md'}
            className={'font-semibold'}
            render={<span />}
          >
            {name}
          </Text>
          {hasConditionalLogic ? (
            <span
              title={'This field has conditional logic applied'}
              className={cn('flex h-full justify-center items-center')}
            >
              <Icon name={'data-flow-03'} size={'sm'} />
            </span>
          ) : null}
          {required ? (
            <Badge variant={'destructive'} size={'sm'} border={true}>
              {'Required'}
            </Badge>
          ) : null}
          {readOnly ? (
            <Badge variant={'neutral'} size={'sm'} border={true}>
              {'Read only'}
            </Badge>
          ) : null}
          {hidden ? (
            <Badge variant={'neutral'} size={'sm'}>
              {'Hidden'}
            </Badge>
          ) : null}
        </div>
        {subtitle || getFieldTypeLabel(type, fieldTypeOptions) ? (
          <Text
            preset={'body-md'}
            className={'text-muted-foreground text-sm'}
            render={<span />}
          >
            {subtitle ?? getFieldTypeLabel(type, fieldTypeOptions)}
          </Text>
        ) : null}
      </div>
      {onEdit ? (
        <Button
          variant={'neutral'}
          style={'ghost'}
          size={'icon'}
          aria-label={`Edit ${name}`}
          className={cn(
            // focus-visible keeps the gear from being an invisible tab stop —
            // it is the keyboard/AT route to the editor that the row click
            // gives pointer users.
            'p-0 size-auto opacity-0 group-hover/field-card:opacity-100 focus-visible:opacity-100 transition-opacity'
          )}
          onClick={onEdit}
        >
          <Icon name={'settings-04'} size={'sm'} />
        </Button>
      ) : null}
      {onDelete ? (
        <ConfirmableDeleteButton
          onConfirm={onDelete}
          size={'icon'}
          showOnGroupHover
          groupName={'field-card'}
        />
      ) : null}
    </div>
  );
};
